import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { blogService } from '../../services/blog.service';
import { FaPlus, FaEdit, FaTrash, FaEye, FaPaperPlane, FaClock, FaHeart, FaComment } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const MyBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchMyBlogs();
  }, [filter]);

  const fetchMyBlogs = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const data = await blogService.getAllBlogs(params);
      
      // Filter to show only current user's blogs
      const myBlogs = data.blogs || [];
      setBlogs(myBlogs);
    } catch (error) {
      toast.error('Failed to fetch blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (blogId) => {
    if (!window.confirm('Submit this blog for approval?')) return;
    
    try {
      await blogService.submitBlog(blogId);
      toast.success('Blog submitted for approval!');
      fetchMyBlogs();
    } catch (error) {
      toast.error('Failed to submit blog');
    }
  };

  const handleDelete = async (blogId) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;
    
    try {
      await blogService.deleteBlog(blogId);
      toast.success('Blog deleted successfully');
      fetchMyBlogs();
    } catch (error) {
      toast.error('Failed to delete blog');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      published: 'bg-blue-100 text-blue-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const icons = {
      draft: <FaEdit className="text-gray-500" />,
      pending: <FaClock className="text-yellow-500" />,
      approved: <FaPaperPlane className="text-green-500" />,
      rejected: <FaTrash className="text-red-500" />,
      published: <FaEye className="text-blue-500" />
    };
    return icons[status] || <FaEdit />;
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">My Blogs</h1>
          <p className="text-gray-600 mt-2">Manage your blog posts</p>
        </div>
        <button
          onClick={() => navigate('/dashboard/blogs/create')}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <FaPlus /> Create New Blog
        </button>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('draft')}
            className={`px-4 py-2 rounded-lg ${filter === 'draft' ? 'bg-gray-600 text-white' : 'bg-gray-100'}`}
          >
            Drafts
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg ${filter === 'pending' ? 'bg-yellow-600 text-white' : 'bg-gray-100'}`}
          >
            Pending
          </button>
          <button
            onClick={() => setFilter('approved')}
            className={`px-4 py-2 rounded-lg ${filter === 'approved' ? 'bg-green-600 text-white' : 'bg-gray-100'}`}
          >
            Approved
          </button>
          <button
            onClick={() => setFilter('published')}
            className={`px-4 py-2 rounded-lg ${filter === 'published' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
          >
            Published
          </button>
          <button
            onClick={() => setFilter('rejected')}
            className={`px-4 py-2 rounded-lg ${filter === 'rejected' ? 'bg-red-600 text-white' : 'bg-gray-100'}`}
          >
            Rejected
          </button>
        </div>
      </div>

      {/* Blogs Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : blogs.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500 text-lg mb-4">No blogs found</p>
          <button
            onClick={() => navigate('/dashboard/blogs/create')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Create Your First Blog
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {blogs.map((blog) => (
            <div key={blog._id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
              {/* Featured Image */}
              {blog.featuredImage && (
                <img
                  src={blog.featuredImage}
                  alt={blog.title}
                  className="w-full h-48 object-cover rounded-t-lg"
                />
              )}
              
              <div className="p-6">
                {/* Status Badge */}
                <div className="flex items-center gap-2 mb-3">
                  {getStatusIcon(blog.status)}
                  <span className={`px-3 py-1 text-xs rounded-full ${getStatusBadge(blog.status)} capitalize`}>
                    {blog.status}
                  </span>
                  {blog.isPublic && blog.status === 'published' && (
                    <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                      Public
                    </span>
                  )}
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold mb-2 line-clamp-2">{blog.title}</h3>

                {/* Excerpt */}
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">{blog.excerpt}</p>

                {/* Meta Info */}
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                  <span className="flex items-center gap-1">
                    <FaEye /> {blog.views || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <FaHeart /> {blog.likes?.length || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <FaComment /> {blog.comments?.length || 0}
                  </span>
                  <span>{blog.readTime || 5} min read</span>
                </div>

                {/* Category & Date */}
                <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                  <span className="bg-gray-100 px-2 py-1 rounded capitalize">{blog.category}</span>
                  <span>{new Date(blog.createdAt).toLocaleDateString()}</span>
                </div>

                {/* Rejection Reason */}
                {blog.status === 'rejected' && blog.rejectionReason && (
                  <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                    <p className="text-xs text-red-800">
                      <strong>Rejection Reason:</strong> {blog.rejectionReason}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  {blog.status === 'draft' && (
                    <>
                      <button
                        onClick={() => navigate(`/dashboard/blogs/edit/${blog._id}`)}
                        className="flex-1 bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleSubmit(blog._id)}
                        className="flex-1 bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 text-sm"
                      >
                        Submit
                      </button>
                    </>
                  )}
                  
                  {blog.status === 'rejected' && (
                    <button
                      onClick={() => navigate(`/dashboard/blogs/edit/${blog._id}`)}
                      className="flex-1 bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 text-sm"
                    >
                      Edit & Resubmit
                    </button>
                  )}

                  {(blog.status === 'approved' || blog.status === 'published') && (
                    <button
                      onClick={() => navigate(`/dashboard/blogs/view/${blog._id}`)}
                      className="flex-1 bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 text-sm"
                    >
                      View
                    </button>
                  )}

                  {blog.status === 'pending' && (
                    <button
                      className="flex-1 bg-gray-300 text-gray-700 px-3 py-2 rounded cursor-not-allowed text-sm"
                      disabled
                    >
                      Waiting Approval
                    </button>
                  )}

                  {/*<button*/}
                  {/*  onClick={() => handleDelete(blog._id)}*/}
                  {/*  className="bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700"*/}
                  {/*>*/}
                  {/*  <FaTrash />*/}
                  {/*</button>*/}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyBlogs;
